/**
 * Payment success page - shown after successful Stripe checkout
 * Automatically verifies the session and refreshes subscription status
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui';
import { useLanguage, useAuthStore } from '@/store';
import { useVerifySession } from '@/hooks';
import { getPaymentSuccessTranslation } from '@/utils';
import type { PaymentSuccessTranslationKey } from '@/utils/translations';
import { ROUTES } from '@/config/routes';
import { BookUpsellCard } from '@/components/features/book/BookCard';

export function PaymentSuccessPage() {
  const language = useLanguage();
  const t = (key: PaymentSuccessTranslationKey) => getPaymentSuccessTranslation(key, language);
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const checkAuth = useAuthStore((state) => state.checkAuth);

  const sessionId = searchParams.get('session_id');

  const verifySession = useVerifySession();

  useEffect(() => {
    const verifyAndRefresh = async () => {
      if (sessionId) {
        verifySession.mutate(
          { sessionId },
          {
            onSuccess: async () => {
              // Refresh user data from backend to get updated is_premium status
              await checkAuth();
              setVerificationStatus('success');
            },
            onError: async () => {
              // Even if verification fails, the webhook should have handled it
              // Still refresh user data and show success
              await checkAuth();
              setVerificationStatus('success');
            },
          }
        );
      } else {
        // No session_id means user accessed this page directly or webhook handled it
        // Still refresh user data to get latest premium status
        await checkAuth();
        setVerificationStatus('success');
      }
    };

    verifyAndRefresh();
  }, [sessionId]);

  // Show loading state while verifying
  if (verificationStatus === 'pending') {
    return (
      <div className="container py-16 max-w-lg mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
            <p className="text-muted-foreground">
              {t('verifyingPayment')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state (rare case)
  if (verificationStatus === 'error') {
    return (
      <div className="container py-16 max-w-lg mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-16 w-16 text-yellow-500" />
            </div>
            <CardTitle className="text-yellow-600">
              {t('verificationPending')}
            </CardTitle>
            <CardDescription>
              {t('paymentReceivedProcessing')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              {t('waitAndRefresh')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Button onClick={() => window.location.reload()}>
                {t('refreshPage')}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/profile">
                  {t('viewProfile')}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-16 max-w-lg mx-auto">
      <Card>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <CheckCircle className="h-16 w-16 text-green-500" />
              <Sparkles className="h-6 w-6 text-yellow-500 absolute -top-1 -right-1 animate-pulse" />
            </div>
          </div>
          <CardTitle className="text-green-600">
            {t('paymentSuccessful')}
          </CardTitle>
          <CardDescription>
            {t('thankYouSubscribing')}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            {t('fullAccessMessage')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
            <Button asChild>
              <Link to={ROUTES.guided}>
                {t('exploreSessions')}
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/profile">
                {t('viewProfile')}
              </Link>
            </Button>
          </div>
          {/* D20: the e-book is bought separately, never inside the subscription checkout */}
          <BookUpsellCard />
        </CardContent>
      </Card>
    </div>
  );
}
